import { Button, DropdownMenu, type MenuGroup } from '@bloom/ui';
import { CircleUser } from 'lucide-react';
import { Link, Outlet, useNavigate } from 'react-router';
import { NAV, SITE_NAME } from '../copy/common';
import { ADMIN_PERMISSIONS, useSession } from '../lib/session';

/** The wordmark: a ticket stub with the name cut into it. */
function Wordmark() {
  return (
    <Link to="/" className="flex items-center gap-2.5 text-ink" aria-label={SITE_NAME}>
      <svg viewBox="0 0 32 32" aria-hidden="true" className="size-8">
        <rect width="32" height="32" rx="9" fill="var(--bl-primary)" />
        <path
          d="M6 11h20v3.2a2.8 2.8 0 0 0 0 5.6V23H6v-3.2a2.8 2.8 0 0 0 0-5.6Z"
          fill="var(--bl-on-primary)"
          fillOpacity="0.94"
        />
        <circle cx="21" cy="17" r="2" fill="var(--bl-accent)" />
      </svg>
      <span className="text-xl font-bold tracking-tight">{SITE_NAME}</span>
    </Link>
  );
}

function AccountMenu() {
  const session = useSession();
  const navigate = useNavigate();
  if (session.user === undefined) return <span className="h-9 w-16" aria-hidden="true" />;
  if (session.user === null) {
    return (
      <Button asChild variant="secondary" size="sm">
        <Link to="/login">{NAV.signIn}</Link>
      </Button>
    );
  }
  const isAdmin = ADMIN_PERMISSIONS.some((permission) => session.canPlatform(permission));
  const groups: MenuGroup[] = [
    {
      id: 'mine',
      items: [
        { id: 'me', label: NAV.myRequests, render: (content) => <Link to="/me">{content}</Link> },
        ...(isAdmin
          ? [
              {
                id: 'admin',
                label: NAV.admin,
                render: (content: React.ReactNode) => <Link to="/admin/queue">{content}</Link>,
              },
            ]
          : []),
      ],
    },
    {
      id: 'out',
      items: [
        {
          id: 'signout',
          label: NAV.signOut,
          onSelect: () => {
            void session.signOut().then(() => navigate('/'));
          },
        },
      ],
    },
  ];
  return (
    <DropdownMenu
      label={NAV.menuLabel}
      groups={groups}
      trigger={
        <Button variant="ghost" size="sm" aria-label={NAV.account}>
          <CircleUser className="size-5" aria-hidden="true" />
          <span>{session.user.displayName ?? NAV.account}</span>
        </Button>
      }
    />
  );
}

export function AppLayout() {
  return (
    <div className="min-h-dvh bg-canvas">
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2"
      >
        {NAV.skip}
      </a>
      <header className="sticky top-0 z-30 border-b border-line bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex max-w-120 items-center justify-between px-4 py-2.5">
          <Wordmark />
          <AccountMenu />
        </div>
        <nav aria-label={SITE_NAME} className="mx-auto flex max-w-120 flex-wrap gap-2 px-4 pb-3">
          <Button asChild size="sm">
            <Link to="/trials/new">{NAV.owner}</Link>
          </Button>
          <Button asChild size="sm" variant="secondary">
            <Link to="/me/profile">{NAV.trier}</Link>
          </Button>
        </nav>
      </header>
      <main id="content" tabIndex={-1} className="outline-none">
        <Outlet />
      </main>
    </div>
  );
}
